import React, { memo } from 'react';
import AIAgentLogo from '../img/agent-ai-text.png';
import NewAILogo from '../img/new-dialog.png';
import SvgIcon from '@home/components/SvgIcon';
import { useHistory, useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import { Conversation } from '@/pages/aigc/pages/Agent/ChatPage/types/history';
import { CopilotMoreMenu } from './CopilotMoreMenu';
import './CopilotHeader.scss';
import { ChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { OrganizationText } from '@/pages/aigc/components/OrganizationPermission';
import { store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { getAgentChatUrl } from '../utils/getAgentChatUrl';
import { COPILOT_REPORT_SESSION_ID_KEY, TCopilotWindowMode } from '../../config/constant';
import iconCopilotDockPin from '@/pages/aigc/assets/svg/icon-copilot-dock-pin.svg';
import iconCopilotFloatPin from '@/pages/aigc/assets/svg/icon-copilot-float-pin.svg';

type DragDirection = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw' | 'move';

/** Copilot 标题栏 Props */
interface ICopilotHeaderProps {
  chatList: ChatList[];
  bookMarkedChats: ChatList[];
  chatListLoading: boolean;
  handleCloseCopilot?: () => void;
  createNewSession?: () => void;
  newSessionID?: string;
  dragResizeBoxRef: React.RefObject<{
    handleMouseDown: (direction: DragDirection, e: React.MouseEvent) => void;
  }>;
  sessionId?: string;
  handleChangeCopilotSession?: (sessionId: string, cacheData?: Conversation[]) => void;
  /** 窗口聚焦回调 - 用于层级切换 */
  onFocus?: () => void;
  /** 报告是否展开 - 用于跳转时决定是否带 template 参数 */
  isReportExpanded?: boolean;
  /** 关闭报告窗口回调 */
  closeReportWindow?: () => void;
  /** 报告归属的会话 ID（即 sourceSessionId） */
  reportSessionId?: string;
  /** 报告数据，用于判断 componentType 以确定跳转时的 template 参数 */
  dsReport?: { componentType?: string; [key: string]: unknown; };
  /** 当前窗口模式：悬浮 / 固定停靠（标题栏图钉） */
  windowMode?: TCopilotWindowMode;
  /** 切换当前窗口模式（仅影响本次打开） */
  onChangeWindowMode?: (mode: TCopilotWindowMode) => void;
  /** 系统默认窗口模式（更多菜单开关） */
  defaultWindowMode?: TCopilotWindowMode;
  /** 切换默认窗口模式（同步当前窗口） */
  onChangeDefaultWindowMode?: (mode: TCopilotWindowMode) => void;
}

/**
 * Copilot 标题栏：拖动区、图钉切换模式、放大/新建/更多/关闭。
 */
// eslint-disable-next-line react/display-name
export const CopilotHeader = memo(
  ({ dragResizeBoxRef, handleChangeCopilotSession, sessionId, newSessionID, handleCloseCopilot, createNewSession, chatList, bookMarkedChats, chatListLoading, onFocus, isReportExpanded, closeReportWindow, reportSessionId, windowMode = 'floating', onChangeWindowMode, defaultWindowMode = 'floating', onChangeDefaultWindowMode, dsReport }: ICopilotHeaderProps) => {
    const history = useHistory();
    const { formatMessage } = useIntl();
    const isDocked = windowMode === 'docked';

    /** 图钉：悬浮 ↔ 内嵌 */
    const handleToggleWindowMode = (): void => {
      onChangeWindowMode?.(isDocked ? 'floating' : 'docked');
    };

    /** 更多菜单内选中历史会话 */
    const handleSelectSession = (selectedSessionId: string): void => {
      handleChangeCopilotSession?.(selectedSessionId);
    };

    return (
      <>
        <div
          className="dbh-agent-title"
          onMouseDown={(e) => {
            onFocus?.();
            const dropdownOverlayRef = document.querySelector(
              '.dba-copilot-history-chat-dropdown-overlay, .dbh-agent-more-menu-overlay, .dbh-agent-more-menu-history-overlay',
            );
            !dropdownOverlayRef && dragResizeBoxRef?.current?.handleMouseDown?.('move', e);
          }}
        >
          <div className={'dbh-agent-title-left'}>
            <img src={AIAgentLogo} alt="AI Agent Logo" />
            <OrganizationText textKey="agentTitle" />
          </div>
          <div className="dbh-agent-title-right">
            <CommonBubble
              placement="top"
              content={formatMessage({
                id: isDocked ? 'dba_copilot_pin_tooltip_floating' : 'dba_copilot_pin_tooltip_docked',
              })}
              overlayClassName="dba-nav-menu-overlay"
            >
              <div
                className="dbh-agent-title-right-icon dbh-agent-window-mode-pin"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleToggleWindowMode}
              >
                <img
                  src={isDocked ? iconCopilotFloatPin : iconCopilotDockPin}
                  alt=""
                  width={20}
                  height={20}
                />
              </div>
            </CommonBubble>
            <CommonBubble placement='top' content={formatMessage({ id: 'dba_new_agent' })} overlayClassName='dba-nav-menu-overlay'>
              <div
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                className={'dbh-agent-title-right-icon'}
              >
                <SvgIcon
                  type="enlarge-opposite"
                  onClick={() => {
                    if (newSessionID) {
                      closeReportWindow?.();
                      store.setItem(COPILOT_REPORT_SESSION_ID_KEY, '');
                      history.push('/agent/home');
                      return;
                    }

                    // 无论报告是否属于当前会话，都保持文件的打开/关闭状态不变
                    // 如果报告窗口是打开的，设置跳转标记，保留报告状态
                    if (isReportExpanded) {
                      // 先触发强制保存
                      store.setItem('__dba_trigger_report_save__', true);
                      store.setItem('__dba_from_copilot__', true);
                      // 关闭 Copilot 聊天窗口（报告窗口保留）
                      handleCloseCopilot?.();

                      history.push(
                        getAgentChatUrl(
                          sessionId!,
                          (dsReport as any)?.componentType,
                          (dsReport as any)?.isCosTag,
                        ),
                        { sessionId },
                      );
                    } else {
                      // 报告窗口是关闭的，直接跳转，保持关闭状态
                      handleCloseCopilot?.();
                      // 如果报告归属于当前会话但窗口是关闭的，保留 sessionId 让 ChatPage 能恢复关闭状态的报告
                      // 如果报告不属于当前会话，清除 sessionId 防止残留状态
                      if (reportSessionId === sessionId) {
                        // 报告归属于当前会话，保留 COPILOT_REPORT_SESSION_ID_KEY 让 ChatPage 自行决定是否显示报告
                      } else {
                        store.setItem(COPILOT_REPORT_SESSION_ID_KEY, '');
                      }
                      history.push(
                        getAgentChatUrl(
                          sessionId!,
                          (dsReport as any)?.componentType,
                          (dsReport as any)?.isCosTag,
                        ),
                        { sessionId },
                      );
                    }
                  }}
                />
              </div>
            </CommonBubble>
            <CommonBubble placement='top' content={formatMessage({ id: 'dba_new_qa' })} overlayClassName='dba-nav-menu-overlay'>
              <img
                src={NewAILogo}
                alt="New AI Logo"
                className={'dbh-agent-title-right-icon'}
                onMouseDown={(event) => {
                  if (newSessionID) return;
                  event.stopPropagation();
                  createNewSession?.();
                }}
              />
            </CommonBubble>
            <CopilotMoreMenu
              chatList={chatList}
              bookMarkedChats={bookMarkedChats}
              chatListLoading={chatListLoading}
              newSessionID={newSessionID}
              sessionId={sessionId}
              onSelectSession={handleSelectSession}
              defaultWindowMode={defaultWindowMode}
              onChangeDefaultWindowMode={onChangeDefaultWindowMode}
            />
            <hr />
            <div
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
            >
              <SvgIcon
                className={'dbh-agent-title-right-icon'}
                type="clonse"
                onClick={() => {
                  handleCloseCopilot?.();
                }}
              />
            </div>
          </div>
        </div>
      </>
    );
  },
);